import { useGetOloWebhookCredentialQuery } from 'api/oloWebhookCredential'
import { useGetOrderStatusWebhookReceiptsQuery } from 'api/orderStatusWebhookReceipts'
import { useMemo } from 'react'
import {
  deriveOloWebhookConnectionState,
  isOloWebhookStale,
  latestOloReceivedAt,
  OloWebhookConnectionState,
} from 'utilities/oloWebhookStatus'

export type UseOloWebhookStatus = {
  /** Undefined until both queries settle without error (and always while
   * disabled) — consumers treat undefined as "don't render a status". */
  connectionState: OloWebhookConnectionState | undefined
  /** ISO timestamp of the most recent Olo webhook across every receipt row, or
   * null when there has never been one (or the queries haven't settled). */
  lastReceivedAt: string | null
  /** Connected, but nothing has arrived in over a day — `connectionState`
   * stays 'connected'; consumers swap to their "needs attention" CTA. */
  needsReconnect: boolean
  webhookUrl: string
}

// Merchant-wide Olo webhook connection status — the single place the two
// status queries are called. Read by LiveUpdates (via useOloWebhookConnection)
// AND the RecoveryAI console alert. FR-36 seam: when an aggregated Olo status
// endpoint lands, swap the source here and both surfaces follow.
export function useOloWebhookStatus(enabled: boolean): UseOloWebhookStatus {
  // Both skipped while disabled so a non-Olo merchant never fires them.
  const credentialQuery = useGetOloWebhookCredentialQuery(undefined, {
    skip: !enabled,
  })
  const receiptsQuery = useGetOrderStatusWebhookReceiptsQuery(undefined, {
    skip: !enabled,
  })

  const latestReceivedAt = useMemo(
    () => latestOloReceivedAt(receiptsQuery.data ?? []),
    [receiptsQuery.data]
  )

  // RTK Query has three non-ready states (uninitialized/loading/error), not
  // two — on the render where `skip` flips false, a query reports
  // isUninitialized before its effect fires. Gating on isSuccess covers all
  // three, so `data: undefined` never reads as a confident "not configured".
  const isConnectionStateReady =
    enabled && credentialQuery.isSuccess && receiptsQuery.isSuccess

  const connectionState = isConnectionStateReady
    ? deriveOloWebhookConnectionState(
        credentialQuery.data?.configured ?? false,
        latestReceivedAt
      )
    : undefined

  // Gated on the 'connected' state, so it inherits the readiness gate above —
  // a mid-load `undefined` can never flash a reconnect CTA.
  const needsReconnect =
    connectionState === 'connected' &&
    isOloWebhookStale(latestReceivedAt, Date.now())

  return {
    connectionState,
    // Held back until the queries settle, for the same reason connectionState
    // is: an in-flight `undefined` would otherwise read as a confident "never".
    lastReceivedAt: isConnectionStateReady ? latestReceivedAt : null,
    needsReconnect,
    webhookUrl: credentialQuery.data?.webhookUrl ?? '',
  }
}
